/* ---------------------------------------------------------------------------
   <td-quote-form> — keeps the hidden variant fields of the Request a Quote
   form in step with the product section's variant picker, so the enquiry email
   names the variant the shopper was actually looking at.

   Markup: snippets/td-quote-form.liquid

   Streamline's product section dispatches a `variantChange` CustomEvent on its
   own container (`[data-section-id]`) with `detail.variant`. We listen on that
   container rather than on `document` so two product sections on one page
   cannot overwrite each other's fields.

   Theme editor re-renders (`shopify:section:load` / `shopify:section:unload`)
   replace the section's DOM, which tears down and re-creates this element, so
   connectedCallback / disconnectedCallback already cover them — no separate
   section event listeners are needed.

   The form degrades gracefully without this script: the hidden fields are
   rendered server-side with the initially selected variant.
--------------------------------------------------------------------------- */

(function () {
  if (customElements.get('td-quote-form')) return;

  /**
   * Custom element wrapping the Request a Quote form. Mirrors the product
   * section's selected variant into the form's hidden variant fields.
   */
  class TdQuoteForm extends HTMLElement {
    constructor() {
      super();
      this.onVariantChange = this.onVariantChange.bind(this);
    }

    /**
     * Caches the hidden variant fields and starts listening for variant
     * changes on the enclosing product section.
     * @returns {void}
     */
    connectedCallback() {
      this.titleField = this.querySelector('[data-td-quote-variant-title]');
      this.idField = this.querySelector('[data-td-quote-variant-id]');
      if (!this.titleField && !this.idField) return;

      // Start from the parent: closest() matches the element itself first.
      this.container = this.parentElement
        ? this.parentElement.closest('[data-section-id]')
        : null;
      if (!this.container) return;

      this.container.addEventListener('variantChange', this.onVariantChange);
    }

    /**
     * Stops listening for variant changes when the element leaves the DOM.
     * @returns {void}
     */
    disconnectedCallback() {
      if (!this.container) return;
      this.container.removeEventListener('variantChange', this.onVariantChange);
    }

    /**
     * Writes the newly selected variant's title and id into the hidden fields.
     * @param {CustomEvent} event - The section's `variantChange` event; the
     *   variant is read from `event.detail.variant`.
     * @returns {void}
     */
    onVariantChange(event) {
      const variant = event && event.detail ? event.detail.variant : null;

      // No variant means the selected combination does not exist. Leave the
      // last known good values in place rather than sending an empty variant.
      if (!variant) return;

      if (this.titleField) this.titleField.value = variant.title || '';
      if (this.idField) this.idField.value = variant.id || '';
    }
  }

  customElements.define('td-quote-form', TdQuoteForm);
})();
